import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCustomerAuth, startGoogleLogin } from "@/context/CustomerAuthContext";
import { resolveImage } from "@/lib/siteDefaults";
import { Package, Heart, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Account() {
  const { user, loading, logout, refresh } = useCustomerAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingWish, setLoadingWish] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      startGoogleLogin("/account");
      return;
    }
    api.get("/customer/orders", { withCredentials: true })
      .then(({ data }) => setOrders(data))
      .catch(() => {})
      .finally(() => setLoadingOrders(false));
    api.get("/customer/wishlist", { withCredentials: true })
      .then(({ data }) => setWishlist(data.products || []))
      .catch(() => {})
      .finally(() => setLoadingWish(false));
  }, [user, loading]);

  const removeFromWishlist = async (pid) => {
    try {
      await api.delete(`/customer/wishlist/${pid}`, { withCredentials: true });
      setWishlist((prev) => prev.filter((p) => p.id !== pid));
      toast.success("Removed from wishlist");
    } catch {
      toast.error("Could not remove");
    }
  };

  const doLogout = async () => {
    await logout();
    await refresh();
    navigate("/");
    toast.success("Signed out");
  };

  if (loading || !user) {
    return (
      <div className="pt-40 pb-40 text-center min-h-screen">
        <p className="font-serif italic text-2xl text-stone-500">Loading your account…</p>
      </div>
    );
  }

  const tabBtn = (v, label, Icon) => (
    <button
      onClick={() => setTab(v)}
      data-testid={`account-tab-${v}`}
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm tracking-wide border transition-all ${
        tab === v
          ? "bg-[#1A1C1A] text-[#F9F9F6] border-[#1A1C1A]"
          : "border-stone-300 text-[#1A1C1A] hover:border-[#1A1C1A]"
      }`}
    >
      <Icon strokeWidth={1.5} className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-32 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-stone-200 pb-8">
          <div className="flex items-center gap-5">
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#F4F1EA] flex items-center justify-center">
                <User strokeWidth={1.5} className="w-7 h-7 text-stone-500" />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Your Account</p>
              <h1 data-testid="account-name" className="font-serif text-4xl md:text-5xl font-light tracking-tight">
                {user.name || user.email.split("@")[0]}
              </h1>
              <p className="text-sm text-stone-500 mt-1">{user.email}</p>
            </div>
          </div>
          <Button data-testid="account-logout" onClick={doLogout} variant="outline" className="rounded-full self-start md:self-auto">
            <LogOut strokeWidth={1.5} className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>

        <div className="mt-8 flex gap-3">
          {tabBtn("orders", `Orders (${orders.length})`, Package)}
          {tabBtn("wishlist", `Wishlist (${wishlist.length})`, Heart)}
        </div>

        <div className="mt-10">
          {tab === "orders" && (
            <div>
              {loadingOrders && <p className="text-stone-500 py-10 text-center">Loading orders…</p>}
              {!loadingOrders && orders.length === 0 && (
                <div className="text-center py-20 border border-dashed border-stone-300 rounded-2xl">
                  <Package strokeWidth={1.25} className="w-10 h-10 mx-auto text-stone-400" />
                  <p className="mt-4 font-serif italic text-2xl text-stone-500">No orders yet.</p>
                  <Link to="/shop" className="mt-6 inline-block bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-8 py-3 rounded-full font-sans text-sm tracking-wider uppercase">
                    Shop the collection
                  </Link>
                </div>
              )}
              {orders.map((o) => (
                <div key={o.session_id} data-testid="order-row" className="border border-stone-200 rounded-2xl p-6 mb-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-stone-500">Order · {new Date(o.created_at).toLocaleDateString()}</p>
                      <p className="font-serif text-2xl mt-1">${((o.amount || 0) / 100).toFixed(2)}</p>
                    </div>
                    <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${
                      o.payment_status === "paid" ? "bg-[#EAE7DC] text-[#3F5F3F]" :
                      o.payment_status === "failed" ? "bg-red-50 text-red-700" :
                      "bg-stone-100 text-stone-600"
                    }`}>
                      {o.payment_status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm text-stone-700">
                        {it.image && <img src={resolveImage(it.image)} alt="" className="w-10 h-12 rounded object-cover" />}
                        <span className="flex-1">{it.name || it.product_id} · qty {it.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "wishlist" && (
            <div>
              {loadingWish && <p className="text-stone-500 py-10 text-center">Loading wishlist…</p>}
              {!loadingWish && wishlist.length === 0 && (
                <div className="text-center py-20 border border-dashed border-stone-300 rounded-2xl">
                  <Heart strokeWidth={1.25} className="w-10 h-10 mx-auto text-stone-400" />
                  <p className="mt-4 font-serif italic text-2xl text-stone-500">No pieces saved yet.</p>
                  <Link to="/shop" className="mt-6 inline-block bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-8 py-3 rounded-full font-sans text-sm tracking-wider uppercase">
                    Discover the collection
                  </Link>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {wishlist.map((p) => (
                  <div key={p.id} data-testid="wishlist-item" className="relative">
                    <Link to={`/product/${p.id}`} className="block">
                      <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4F1EA] mb-3">
                        {p.images?.[0] && <img src={resolveImage(p.images[0])} alt={p.name} className="w-full h-full object-cover" />}
                      </div>
                      <h3 className="font-serif text-lg font-light tracking-tight">{p.name}</h3>
                      <p className="font-sans text-sm text-stone-600">${p.price.toFixed(2)}</p>
                    </Link>
                    <button
                      onClick={() => removeFromWishlist(p.id)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-sm"
                      aria-label="Remove from wishlist"
                      data-testid={`wishlist-remove-${p.id}`}
                    >
                      <Heart strokeWidth={1.5} className="w-4 h-4 fill-[#1A1C1A] text-[#1A1C1A]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
